import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useToast } from '@/hooks/use-toast';

interface Profile {
  id: string;
  full_name: string | null;
  company_name: string | null;
  phone: string | null;
  cpf_cnpj?: string | null;
  avatar_url?: string | null;
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  cep?: string | null;
  privacy_policy_accepted?: boolean | null;
  privacy_policy_accepted_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface UseAuthReturn {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string; data?: { user: User | null; session: Session | null } }>;
  signUp: (email: string, password: string, fullName?: string, companyName?: string, phone?: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error?: string }>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  updateUserPassword: (password: string) => Promise<{ error?: string }>;
  isPasswordRecovery: boolean;
}

export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const { toast } = useToast();

  // Buscar perfil do usuário
  const fetchProfile = async (userId: string) => {
    try {
      console.log(`🔒 useAuth - Buscando perfil para usuário: ${userId}`);

      // Como há um erro de tipo na tabela profiles, vamos usar o método genérico de busca
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('🔒 useAuth - Erro ao buscar perfil:', error);
        return;
      }

      console.log('🔒 useAuth - Perfil encontrado:', data);
      const current = data as unknown as Profile
      setProfile(current);
      try {
        const companyMeta = (session?.user as any)?.user_metadata?.company_name
        if (!current.company_name && companyMeta) {
          await supabase.from('profiles').update({ company_name: companyMeta, updated_at: new Date().toISOString() }).eq('id', userId)
          setProfile(prev => prev ? { ...prev, company_name: companyMeta } as Profile : prev)
        }
      } catch { }
    } catch (error) {
      console.error('🔒 useAuth - Erro ao buscar perfil:', error);
    }
  };

  // Função auxiliar para verificar e atualizar a sessão
  const refreshSession = async () => {
    console.log('🔒 useAuth - Atualizando sessão');
    setLoading(true);

    try {
      const { data: sessionData, error } = await supabase.auth.getSession();

      if (error) {
        console.error('🔒 useAuth - Erro ao obter sessão:', error);
        setUser(null);
        setSession(null);
      } else if (sessionData?.session) {
        console.log('🔒 useAuth - Sessão atualizada:', sessionData.session.user.id);
        setSession(sessionData.session);
        setUser(sessionData.session.user);

        if (sessionData.session.user) {
          fetchProfile(sessionData.session.user.id);
        }
      } else {
        console.log('🔒 useAuth - Nenhuma sessão ativa');
        setUser(null);
        setSession(null);
      }
    } catch (e) {
      console.error('🔒 useAuth - Erro ao atualizar sessão:', e);
    } finally {
      setLoading(false);
    }
  };

  // Configurar listeners de autenticação
  useEffect(() => {
    console.log('🔒 useAuth - Inicializando hook de autenticação');

    let isInitialized = false;

    // Configurar listener de mudanças de estado
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log(`🔒 useAuth - Evento de autenticação: ${event}`);

        // Tratamento específico para recuperação de senha
        if (event === 'PASSWORD_RECOVERY') {
          console.log('🔒 useAuth - Modo de recuperação de senha detectado');
          setIsPasswordRecovery(true);
        }

        // Na primeira execução, verificar a sessão atual
        if (!isInitialized) {
          isInitialized = true;

          if (session) {
            console.log(`🔒 useAuth - Sessão inicial encontrada: ${session.user.id}`);
            setSession(session);
            setUser(session.user);
            fetchProfile(session.user.id);
          } else {
            console.log('🔒 useAuth - Nenhuma sessão inicial encontrada');
          }
          setLoading(false);
          return;
        }

        // Para eventos subsequentes
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'PASSWORD_RECOVERY') {
          if (session) {
            console.log(`🔒 useAuth - Usuário autenticado: ${session.user.id}`);
            setSession(session);
            setUser(session.user);
            fetchProfile(session.user.id);
          }
        } else if (event === 'SIGNED_OUT') {
          console.log('🔒 useAuth - Usuário desconectado');
          setUser(null);
          setSession(null);
          setProfile(null);
          setIsPasswordRecovery(false);
        }

        setLoading(false);
      }
    );

    return () => {
      console.log('🔒 useAuth - Limpando subscription');
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);

      // Verificar se é um usuário diferente do anterior logando
      const previousUserId = localStorage.getItem('player_cache_user_id');

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('❌ [useAuth] Erro no signIn:', error);
        console.error('❌ [useAuth] Detalhes do erro:', JSON.stringify(error, null, 2));
        throw error;
      }

      // Se o novo usuário é diferente do anterior, limpar o cache de mídias
      if (data.user && previousUserId && previousUserId !== data.user.id) {
        console.log('🔄 [useAuth] Usuário diferente detectado no login, limpando cache...');
        console.log('🔄 [useAuth] Usuário anterior:', previousUserId);
        console.log('🔄 [useAuth] Novo usuário:', data.user.id);

        // Limpar IndexedDB (cache de mídias)
        if ('indexedDB' in window) {
          try {
            indexedDB.deleteDatabase('watch-media-cache-v2');
            console.log('✅ [useAuth] Cache IndexedDB limpo com sucesso no login');
          } catch (e) {
            console.error('❌ [useAuth] Erro ao limpar cache IndexedDB:', e);
          }
        }

        // Limpar sessionStorage de dados do player
        sessionStorage.removeItem('player_activated_user_id');
        sessionStorage.removeItem('player_preview_user_id');
        sessionStorage.removeItem('player_device_code');
        sessionStorage.removeItem('player_activated_by_companion');
      }

      // Salvar novo userId para comparação futura
      if (data.user) {
        localStorage.setItem('player_cache_user_id', data.user.id);
      }

      toast({
        title: "Login realizado com sucesso!",
        description: "Bem-vindo de volta ao sistema.",
      });

      // Retornar data com user info para permitir limpeza do cache
      return { data };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro no login';

      toast({
        title: "Erro no login",
        description: errorMessage,
        variant: "destructive",
      });

      return { error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, fullName?: string, companyName?: string, phone?: string) => {
    try {
      setLoading(true);
      const redirectUrl = `${window.location.origin}/`;

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
            company_name: companyName,
            phone: phone,
          }
        }
      });

      if (error) throw error;

      toast({
        title: "Cadastro realizado!",
        description: "Verifique seu email para confirmar a conta.",
      });

      return {};
    } catch (error) {
      let errorMessage = error instanceof Error ? error.message : 'Erro no cadastro';

      // Tradução de erros comuns do Supabase
      if (errorMessage === "User already registered") errorMessage = "Este e-mail já está cadastrado.";
      if (errorMessage.includes("Password should be at least")) errorMessage = "A senha deve ter pelo menos 6 caracteres.";
      if (errorMessage === "Invalid login credentials") errorMessage = "E-mail ou senha incorretos.";

      toast({
        title: "Erro no cadastro",
        description: errorMessage,
        variant: "destructive",
      });

      return { error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      console.log('🔒 useAuth - Iniciando logout...');

      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error('❌ useAuth - Erro no logout:', error);
        throw error;
      }

      console.log('✅ useAuth - Logout realizado com sucesso');

      // Limpar IndexedDB (cache de mídias)
      if ('indexedDB' in window) {
        try {
          indexedDB.deleteDatabase('watch-media-cache-v2');
          console.log('✅ [useAuth] Cache IndexedDB limpo com sucesso no logout');
        } catch (e) {
          console.error('❌ [useAuth] Erro ao limpar cache IndexedDB:', e);
        }
      }

      // 🔒 SEGURANÇA: Limpar apenas dados específicos do app (não todo o storage)
      // Isso evita afetar outros apps/sites no mesmo domínio

      // Limpar localStorage - apenas chaves do Watch
      const watchLocalStorageKeys = [
        'player_cache_user_id',
        'player_activated_by_companion'
      ];
      watchLocalStorageKeys.forEach(key => localStorage.removeItem(key));

      // Limpar sessionStorage - apenas chaves do Watch  
      const watchSessionStorageKeys = [
        'player_activated_user_id',
        'player_preview_user_id',
        'player_device_code',
        'player_activated_by_companion'
      ];
      watchSessionStorageKeys.forEach(key => sessionStorage.removeItem(key));

      console.log('🧹 useAuth - Cache do Watch limpo (preservando outros dados)');

      toast({
        title: "Logout realizado",
        description: "Até logo!",
      });
    } catch (error) {
      console.error('❌ useAuth - Erro no logout:', error);
      toast({
        title: "Erro no logout",
        description: "Tente novamente",
        variant: "destructive",
      });
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const redirectUrl = `${window.location.origin}/auth`;

      console.log('🔑 Tentando enviar email de recuperação de senha para:', email);
      console.log('🔗 URL de redirecionamento:', redirectUrl);

      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      console.log('📧 Resposta do Supabase:', { data, error });

      if (error) throw error;

      toast({
        title: "Email enviado!",
        description: "Verifique sua caixa de entrada para redefinir sua senha.",
      });

      return {};
    } catch (error) {
      console.error('❌ Erro ao enviar email de recuperação:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao enviar email de recuperação';

      toast({
        title: "Erro ao enviar email",
        description: errorMessage,
        variant: "destructive",
      });

      return { error: errorMessage };
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: 'Usuário não autenticado' };

    try {
      console.log('🔍 [useAuth] Iniciando updateProfile para user:', user.id);
      console.log('🔍 [useAuth] Updates recebidos:', updates);

      const updateData = { ...updates, updated_at: new Date().toISOString() };
      console.log('🔍 [useAuth] Dados para enviar ao Supabase:', updateData);

      const { data, error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)
        .select('*')
        .single();

      if (error) {
        console.error('❌ [useAuth] Erro no updateProfile:', error);
        console.error('❌ [useAuth] Erro message:', error.message);
        console.error('❌ [useAuth] Erro details:', error.details);
        console.error('❌ [useAuth] Erro hint:', error.hint);
        console.error('❌ [useAuth] Erro code:', error.code);
        throw error;
      }

      if (data) {
        console.log('🔍 [useAuth] Profile atualizado no banco, company_name:', data.company_name);
        setProfile(data as unknown as Profile);
      } else {
        console.log('🔍 [useAuth] Profile atualizado localmente, updates:', updates);
        setProfile(prev => prev ? { ...prev, ...updates } : prev);
      }

      toast({
        title: "Perfil atualizado",
        description: "Suas informações foram salvas com sucesso.",
      });

      return {};
    } catch (error) {
      console.error('❌ [useAuth] Erro catch:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao atualizar perfil';

      toast({
        title: "Erro ao atualizar perfil",
        description: errorMessage,
        variant: "destructive",
      });

      return { error: errorMessage };
    }
  };

  const updateUserPassword = async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) throw error;

      toast({
        title: "Senha atualizada!",
        description: "Sua senha foi alterada com sucesso.",
      });

      return {};
    } catch (error) {
      console.error('❌ [useAuth] Erro ao atualizar senha:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao atualizar senha';

      toast({
        title: "Erro ao atualizar senha",
        description: errorMessage,
        variant: "destructive",
      });

      return { error: errorMessage };
    }
  };

  return {
    user,
    session,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    resetPassword,
    isPasswordRecovery,
    updateUserPassword,
  };
};
